import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { UserCog } from 'lucide-react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { PageLayout } from '../../../components/ui/PageLayout';
import { LoadingState } from '../../../components/AsyncStates';
import RefereeListPanel from './components/RefereeListPanel';
import MatchDetailsPanel from './components/MatchDetailsPanel';
import AssignmentActionPanel from './components/AssignmentActionPanel';
import ConfirmAssignmentModal from './components/ConfirmAssignmentModal';
import { useReferees } from '@/hooks/useReferees';
import { useMatches } from '@/hooks/useMatches';
import { useRefereeRecommendations } from '@/hooks/useRefereeRecommendations';
import { getReferee } from '@/api/TournamentManagement/refereeApi';
import { RefereeCertification } from '@/models/tournament';
import type {
  RefereeView,
  MatchView,
  RefereeListQuery,
  MatchListQuery,
  RefereeRecommendationView,
} from '@/models/tournament';

const RefereeAssignmentCenter: React.FC = () => {
  const { t } = useTranslation('tournament');
  const [selectedMatch, setSelectedMatch] = useState<MatchView | null>(null);
  const [selectedReferee, setSelectedReferee] = useState<RefereeView | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [certificationFilter, setCertificationFilter] = useState<string>('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [assignmentType, setAssignmentType] = useState<'new' | 'reassignment'>('new');

  // Referee query
  const refereeQuery: RefereeListQuery = useMemo(() => ({
    search: searchTerm || undefined,
    certification: certificationFilter !== '' ? (parseInt(certificationFilter) as RefereeCertification) : undefined,
    pageSize: 50, // Load enough referees for the panel
  }), [searchTerm, certificationFilter]);

  const { data: refereeData, loading: refereesLoading, refetch: refetchReferees } = useReferees(refereeQuery);

  // Match query - load matches that need assignment (Scheduled or InProgress)
  const matchQuery: MatchListQuery = useMemo(() => ({
    pageSize: 50,
  }), []);

  const { data: matchData, loading: matchesLoading } = useMatches(matchQuery);

  // Fetch recommendations for the selected match
  const { data: recommendations, loading: recommendationsLoading } = useRefereeRecommendations(
    selectedMatch?.id ?? null
  );

  const certificationKeys: Record<RefereeCertification, string> = {
    [RefereeCertification.NationalLevelA]: 'National Level A',
    [RefereeCertification.AFCElite]: 'AFC Elite',
    [RefereeCertification.FIFAInternational]: 'FIFA International',
  };

  const referees = refereeData?.items ?? [];
  const matches = matchData?.items ?? [];
  const totalReferees = refereeData?.totalCount ?? 0;
  const availableCount = refereeData?.availableCount ?? 0;

  const handleRefereeSelect = (referee: RefereeView) => {
    setSelectedReferee(referee);
  };

  const handleMatchSelect = (match: MatchView) => {
    setSelectedMatch(match);
    setSelectedReferee(null);
  };

  const handleAssignReferee = () => {
    if (!selectedMatch || !selectedReferee) return;

    // Determine assignment type
    if (selectedMatch.refereeTeamMembers?.length > 0) {
      setAssignmentType('reassignment');
    } else {
      setAssignmentType('new');
    }

    setShowConfirmModal(true);
  };

  const handleQuickAssign = async (refereeProfileId: number) => {
    try {
      const result = await getReferee(refereeProfileId);
      if (result.errors.length === 0 && result.payload) {
        setSelectedReferee(result.payload);
        setAssignmentType(selectedMatch?.refereeTeamMembers?.length ? 'reassignment' : 'new');
        setShowConfirmModal(true);
      }
    } catch (err) {
      console.error('Failed to fetch referee for quick assign:', err);
    }
  };

  const handleConfirmAssignment = () => {
    setShowConfirmModal(false);
    setSelectedMatch(null);
    setSelectedReferee(null);
    refetchReferees();
  };

  const clearFilters = () => {
    setSearchTerm('');
    setCertificationFilter('');
  };

  const hasActiveFilters = searchTerm || certificationFilter;

  return (
    <PageLayout
      title={t('Referee Assignment')}
      description={t('Manage referee assignments with intelligent recommendations and availability tracking')}
      icon={UserCog}
      accentColor="amber"
      maxWidth="1600px"
      stats={[
        { label: t('Total Referees'), value: totalReferees },
        { label: t('Available Today'), value: availableCount, color: 'text-green-600' },
      ]}
      statsColumns={2}
    >
        {/* Filters */}
        <div className="bg-card border border-border rounded-lg p-4 mb-6" data-testid="referee-filters">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <Input
                placeholder={t('Search referees by name, ID, or location...')}
                value={searchTerm}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                icon="Search"
                data-testid="referee-search-input"
              />
            </div>
            <Select
              value={certificationFilter}
              onChange={setCertificationFilter}
              options={[
                { value: '', label: t('All Certifications') },
                { value: '2', label: t('FIFA International') },
                { value: '1', label: t('AFC Elite') },
                { value: '0', label: t('National Level A') },
              ]}
              data-testid="referee-certification-filter"
            />
          </div>
          {hasActiveFilters && (
            <div className="mt-3 flex items-center justify-between">
              <p className="text-sm text-gray-600">
                {t('Showing {{count}} of {{total}} referees', { count: referees.length, total: totalReferees })}
              </p>
              <button
                onClick={clearFilters}
                className="text-sm text-primary hover:text-primary/80 font-medium focus-visible:ring-2 focus-visible:ring-primary/20 rounded"
                data-testid="referee-clear-filters-btn"
              >
                {t('Clear Filters')}
              </button>
            </div>
          )}
        </div>

        {/* Loading State */}
        {(refereesLoading || matchesLoading) && (
          <div className="py-12">
            <LoadingState title={t('Loading data...')} size="md" />
          </div>
        )}

        {/* Three-Panel Layout */}
        {!refereesLoading && !matchesLoading && (
          <div className="grid grid-cols-12 gap-6">
            {/* Left Panel - Referee List */}
            <div className="col-span-12 lg:col-span-3">
              <RefereeListPanel
                referees={referees}
                selectedReferee={selectedReferee}
                onSelectReferee={handleRefereeSelect}
              />
            </div>

            {/* Center Panel - Match Details */}
            <div className="col-span-12 lg:col-span-6">
              <MatchDetailsPanel
                matches={matches}
                selectedMatch={selectedMatch}
                onSelectMatch={handleMatchSelect}
              />
            </div>

            {/* Right Panel - Assignment Actions */}
            <div className="col-span-12 lg:col-span-3 space-y-6">
              <AssignmentActionPanel
                selectedMatch={selectedMatch}
                selectedReferee={selectedReferee}
                onAssign={handleAssignReferee}
              />

              {/* Best Fit Recommendations */}
              {selectedMatch && ((recommendations && recommendations.length > 0) || recommendationsLoading) && (
                <div className="bg-card border border-border rounded-lg p-4" data-testid="best-fit-recommendations">
                  <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
                    <Icon name="Target" size={20} className="me-2 text-primary" />
                    {t('Best Fit Recommendations')}
                  </h3>

                  {recommendationsLoading ? (
                    <div className="py-6">
                      <LoadingState title={t('Loading recommendations...')} size="sm" />
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {recommendations?.map((referee, index) => (
                        <div key={referee.refereeProfileId ?? index} className="border border-border rounded-lg p-3 hover:border-primary/50 transition-colors">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center space-x-2 rtl:space-x-reverse">
                              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                                index === 0 ? 'bg-yellow-100 text-yellow-800' :
                                index === 1 ? 'bg-gray-100 text-gray-800' : 'bg-orange-100 text-orange-800'
                              }`}>
                                {referee.rank ?? index + 1}
                              </div>
                              <div>
                                <p className="text-sm font-semibold text-foreground">{referee.name}</p>
                                <p className="text-xs text-muted-foreground">
                                  {t(certificationKeys[referee.certification] ?? 'Unknown')}
                                </p>
                              </div>
                            </div>
                            <div className="text-end">
                              <p className="text-xs text-muted-foreground">{t('Fit Score')}</p>
                              <p className="text-lg font-bold text-primary">{referee.fitScore}</p>
                            </div>
                          </div>

                          {/* Estimated Work Points */}
                          {referee.estimatedWorkPoints > 0 && (
                            <div className="mb-2" data-testid={`est-work-points-${referee.refereeProfileId}`}>
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-blue-50 text-blue-700 border border-blue-200">
                                <Icon name="Zap" size={10} className="me-1" />
                                {t('Est. work points', { points: referee.estimatedWorkPoints })}
                              </span>
                            </div>
                          )}

                          <div className="flex flex-wrap gap-1.5">
                            {referee.fitReasons?.map((reason, idx) => (
                              <span key={idx} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-green-50 text-green-700 border border-green-200">
                                <Icon name="CheckCircle" size={10} className="me-1" />
                                {reason}
                              </span>
                            ))}
                          </div>

                          <div className="mt-2 pt-2 border-t border-border">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleQuickAssign(referee.refereeProfileId);
                              }}
                              className="w-full px-3 py-1.5 text-xs font-medium text-white bg-primary rounded-md hover:bg-primary/90 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20"
                              data-testid={`quick-assign-btn-${referee.refereeProfileId}`}
                            >
                              <Icon name="UserPlus" size={12} className="inline me-1" />
                              {t('Quick Assign')}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

      {/* Confirmation Modal */}
      <ConfirmAssignmentModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmAssignment}
        match={selectedMatch}
        referee={selectedReferee}
        assignmentType={assignmentType}
      />
    </PageLayout>
  );
};

export default RefereeAssignmentCenter;
